import { decorate, action, computed, observable, runInAction } from 'mobx';
import i18n from 'i18next';

import queries from '../queries';
import bundleConfig from '../bundleConfig';
import { extractNodes } from '../utils/graphqlMappers';
import {
    findClosestTranslation,
    findLocaleByCode,
    findLocaleForLanguage,
    resolveDefaultLocale,
} from '../utils/localeHelpers';
import { getQueryParams } from '../utils/url';
import configStore from './configStore';
import { contentLocalePreferenceStore } from './localStorage';

/**
 * Notifies registered handlers of a Locale change, for hosts that can rebuild
 * themselves in place without navigating (the embedded widget bundle and the
 * full WebLink page). If no handler is registered the change is ignored —
 * every consumer that exposes a Locale picker registers one.
 */
let onContentLocaleChanged = null;

/**
 * Points the site language at the same Locale the course content is shown in:
 * translations switch to the closest bundle we ship - "fr" for the "fr-FR"
 * Locale - while date formatting and locale-tagged requests follow the Locale
 * itself. If that language has no bundle, translations fall back to English.
 */
const syncSiteLanguage = (code) => {
    // A host that pinned the interface language keeps it; the picker still
    // drives course content.
    if (bundleConfig.locale) return;

    configStore.setLocale(code);

    const language = findClosestTranslation(
        code,
        i18n.isInitialized ? Object.keys(i18n.store.data) : [],
    );

    if (language && i18n.language !== language) {
        // The promise resolves long after the change has applied; nothing
        // waits for it.
        i18n.changeLanguage(language);
    }
};

export const setContentLocaleChangeHandler = (handler) => {
    onContentLocaleChanged = handler;
};

/**
 * Tracks the Locale that course content is requested in. The visitor's choice
 * is kept in local storage for this browser only, and also drives the site
 * language, so translations and date formatting follow the picker. Both start
 * from the same sources: the ?locale= querystring parameter, then the browser.
 */
class ContentLocaleStore {
    constructor() {
        runInAction(() => {
            this.locales = [];
            this.selectedCode = null;
            this.configuredCode = null;
        });
    }

    setLocales = action((locales) => {
        this.locales = locales;
        // Read here rather than in the constructor: the key is scoped to the Portal
        // domain, which is not known until the first operation has gone out.
        this.selectedCode = contentLocalePreferenceStore.getContentLocaleCode();

        const { locale: querystringLocale } = getQueryParams();
        if (querystringLocale) {
            // ?locale= picks the site language through i18next detection; the
            // content Locale follows it for as long as the parameter is in the
            // URL. Not persisted - a link is an instruction for the visit, not
            // the visitor's own choice.
            const localeMatch = findLocaleForLanguage(locales, querystringLocale);
            if (localeMatch) {
                this.selectedCode = localeMatch.code;
            }
        }

        // Bring the site language back in line with the Locale in effect:
        // i18next resolved it from the querystring or browser, which know
        // nothing of a persisted or newly picked Locale.
        const resolved = findLocaleByCode(locales, this.selectedCode);
        if (resolved) {
            syncSiteLanguage(resolved.code);
        }
    });

    /**
     * The Locale an embedded integration starts in, for host pages that know
     * which language they are written in. It is a default rather than a pin: a
     * visitor who has chosen a Locale keeps it.
     *
     * Deliberately not persisted, so that configuring a widget never writes to
     * the preference the hosted storefront reads on the same Portal domain.
     */
    setConfiguredContentLocale = action((code) => {
        this.configuredCode = code;
    });

    /**
     * Preference is
     * 1. The Locale picked in the nav bar - seeded from ?locale= on this page
     *    load, picked in this session, or persisted from an earlier visit
     * 2. The locale configured in the widget config
     * 3. The browser locale
     */
    get contentLocale() {
        return (
            findLocaleByCode(this.locales, this.selectedCode) ??
            findLocaleByCode(this.locales, this.configuredCode) ??
            resolveDefaultLocale(this.locales)
        );
    }

    get contentLocaleId() {
        // With fewer than two Locales there is no picker and nothing can be
        // translated, so don't ask the Core API for translations at all.
        if (this.locales.length < 2) {
            return undefined;
        }

        return this.contentLocale?.id;
    }

    /**
     * Course content is spread across cached queries and already-rendered pages,
     * so everything holding it is rebuilt rather than refetched selectively. The
     * header is read per operation, so the new Locale applies from the first
     * query afterwards.
     *
     * The choice is persisted before the rebuild because reloading the Locales
     * re-reads it from local storage, so an in-memory-only value would be lost.
     * The site language follows the pick, so the interface changes with the
     * content.
     */
    setContentLocale = action((locale) => {
        // A picker choice replaces the link's locale without changing other URL state.
        const url = new URL(window.location.href);
        url.searchParams.delete('locale');
        window.history.replaceState(window.history.state, '', url.toString());

        contentLocalePreferenceStore.setContentLocaleCode(locale.code);
        this.selectedCode = locale.code;
        syncSiteLanguage(locale.code);

        if (onContentLocaleChanged) {
            onContentLocaleChanged(locale);
            return;
        }

        window.location.reload();
    });
}

decorate(ContentLocaleStore, {
    locales: observable,
    selectedCode: observable,
    configuredCode: observable,
    contentLocale: computed,
    contentLocaleId: computed,
});

const contentLocaleStore = new ContentLocaleStore();

/**
 * Loads the supported Locales onto the store. Must resolve before any course
 * content is queried, otherwise the first page would render in the Locale the
 * content was authored in.
 *
 * When the instance's multilingual courses feature flag is off the Locales are
 * left empty, which hides the picker and stops the Locale header being sent.
 *
 * Failures are swallowed - without a list of Locales there is no choice to
 * offer, and content stays in the Locale it was authored in.
 */
export const loadContentLocales = (client) =>
    client
        .query({ query: queries.locales.getLocales })
        .then(({ data }) => {
            if (!data?.store?.multilingualCoursesEnabled) return;
            contentLocaleStore.setLocales(extractNodes(data?.locales));
        })
        .catch((error) => {
            // eslint-disable-next-line no-console
            console.error(error);
        });

export default contentLocaleStore;
